import { Container, Grid, Paper, Stack, Text, Title, ThemeIcon, Group, Button, Divider } from "@mantine/core";
import {
   IconReportAnalytics,
   IconCreditCard,
   IconTrendingUp,
   IconCurrencyDollar,
   IconReceipt,
   IconWallet,
   IconArrowRight,
   IconShield,
   IconSettings,
   IconCalendar,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const financialReports = [
   {
      title: "Loan Portfolio",
      description: "Comprehensive overview of all loans, including active, closed, and outstanding balances",
      icon: IconCreditCard,
      color: "#699269",
      path: "/dashboard/reports/loan-portfolio",
   },
   {
      title: "Disbursements",
      description: "Track all loan disbursements with detailed analytics and trends",
      icon: IconTrendingUp,
      color: "#558B2F",
      path: "/dashboard/reports/disbursements",
   },
   {
      title: "Repayments",
      description: "Monitor repayment performance, rates, and collection analytics",
      icon: IconReceipt,
      color: "#33691E",
      path: "/dashboard/reports/repayments",
   },
   {
      title: "Revenue",
      description: "View total revenue from interest income and fees",
      icon: IconCurrencyDollar,
      color: "#1B5E20",
      path: "/dashboard/reports/revenue",
   },
   {
      title: "Transactions",
      description: "Complete transaction history.",
      icon: IconWallet,
      color: "#0D3E1F",
      path: "/dashboard/reports/transactions",
   },
   {
      title: "Fee Collection",
      description: "Detailed breakdown of all fees",
      icon: IconReportAnalytics,
      color: "#699269",
      path: "/dashboard/reports/fee-collection",
   },
];

const riskComplianceReports = [
   {
      title: "Risk & Compliance",
      description: "Monitor overdue loans, defaults, high-risk loans, and KYC/BVN verification status",
      icon: IconShield,
      color: "#DC2626",
      path: "/dashboard/reports/risk-compliance",
   },
];

const operationalReports = [
   {
      title: "Operational",
      description: "Track loan processing metrics, approval rates, user activity, and system performance",
      icon: IconSettings,
      color: "#2563EB",
      path: "/dashboard/reports/operational",
   },
   {
      title: "Loan Scheduling",
      description: "Monitor payment schedules, upcoming payments, overdue payments, and repayment progress",
      icon: IconCalendar,
      color: "#8B5CF6",
      path: "/dashboard/reports/loan-scheduling",
   },
];

export default function Reports() {
   const navigate = useNavigate();

   return (
      <Container size="xl" py="xl">
         <Stack gap="xl">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
               <Group gap="md" mb="lg">
                  <ThemeIcon size={48} radius="md" style={{ background: "#699269" }}>
                     <IconReportAnalytics size={24} color="white" />
                  </ThemeIcon>
                  <div>
                     <Title order={2} fw={700} style={{ color: "#1a1a1a" }}>
                        Reports Dashboard
                     </Title>
                     <Text size="sm" c="dimmed">
                        Comprehensive analytics and insights across all areas
                     </Text>
                  </div>
               </Group>
            </motion.div>

            {/* Financial Reports Section */}
            <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.1 }}
            >
               <Title order={3} fw={600} mb="md" style={{ color: "#699269" }}>
                  Financial Reports
               </Title>
               <Grid gutter="lg" mb="xl">
                  {financialReports.map((card, index) => {
                     const IconComponent = card.icon;
                     return (
                        <Grid.Col key={card.path} span={{ base: 12, sm: 6, md: 4 }}>
                           <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                           >
                              <Paper
                                 shadow="sm"
                                 p="xl"
                                 radius="md"
                                 style={{
                                    border: "1px solid #e5e7eb",
                                    cursor: "pointer",
                                    height: "100%",
                                    transition: "all 0.2s ease",
                                 }}
                                 onClick={() => navigate(card.path)}
                                 onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = card.color;
                                    e.currentTarget.style.boxShadow = `0 4px 12px ${card.color}20`;
                                 }}
                                 onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                 }}
                              >
                                 <Stack gap="md">
                                    <ThemeIcon size={56} radius="md" style={{ background: card.color }}>
                                       <IconComponent size={28} color="white" />
                                    </ThemeIcon>
                                    <div>
                                       <Title order={4} fw={600} mb="xs">
                                          {card.title}
                                       </Title>
                                       <Text size="sm" c="dimmed" lineClamp={2}>
                                          {card.description}
                                       </Text>
                                    </div>
                                    <Button
                                       variant="light"
                                       rightSection={<IconArrowRight size={16} />}
                                       style={{ color: card.color }}
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(card.path);
                                       }}
                                    >
                                       View Report
                                    </Button>
                                 </Stack>
                              </Paper>
                           </motion.div>
                        </Grid.Col>
                     );
                  })}
               </Grid>
            </motion.div>

            <Divider my="xl" />

            {/* Risk & Compliance Reports Section */}
            <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.2 }}
            >
               <Title order={3} fw={600} mb="md" style={{ color: "#DC2626" }}>
                  Risk & Compliance Reports
               </Title>
               <Grid gutter="lg" mb="xl">
                  {riskComplianceReports.map((card, index) => {
                     const IconComponent = card.icon;
                     return (
                        <Grid.Col key={card.path} span={{ base: 12, sm: 6, md: 4 }}>
                           <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                           >
                              <Paper
                                 shadow="sm"
                                 p="xl"
                                 radius="md"
                                 style={{
                                    border: "1px solid #e5e7eb",
                                    cursor: "pointer",
                                    height: "100%",
                                    transition: "all 0.2s ease",
                                 }}
                                 onClick={() => navigate(card.path)}
                                 onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = card.color;
                                    e.currentTarget.style.boxShadow = `0 4px 12px ${card.color}20`;
                                 }}
                                 onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                 }}
                              >
                                 <Stack gap="md">
                                    <ThemeIcon size={56} radius="md" style={{ background: card.color }}>
                                       <IconComponent size={28} color="white" />
                                    </ThemeIcon>
                                    <div>
                                       <Title order={4} fw={600} mb="xs">
                                          {card.title}
                                       </Title>
                                       <Text size="sm" c="dimmed" lineClamp={2}>
                                          {card.description}
                                       </Text>
                                    </div>
                                    <Button
                                       variant="light"
                                       rightSection={<IconArrowRight size={16} />}
                                       style={{ color: card.color }}
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(card.path);
                                       }}
                                    >
                                       View Report
                                    </Button>
                                 </Stack>
                              </Paper>
                           </motion.div>
                        </Grid.Col>
                     );
                  })}
               </Grid>
            </motion.div>

            <Divider my="xl" />

            {/* Operational Reports Section */}
            <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.3 }}
            >
               <Title order={3} fw={600} mb="md" style={{ color: "#2563EB" }}>
                  Operational Reports
               </Title>
               <Grid gutter="lg">
                  {operationalReports.map((card, index) => {
                     const IconComponent = card.icon;
                     return (
                        <Grid.Col key={card.path} span={{ base: 12, sm: 6, md: 4 }}>
                           <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                           >
                              <Paper
                                 shadow="sm"
                                 p="xl"
                                 radius="md"
                                 style={{
                                    border: "1px solid #e5e7eb",
                                    cursor: "pointer",
                                    height: "100%",
                                    transition: "all 0.2s ease",
                                 }}
                                 onClick={() => navigate(card.path)}
                                 onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = card.color;
                                    e.currentTarget.style.boxShadow = `0 4px 12px ${card.color}20`;
                                 }}
                                 onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "#e5e7eb";
                                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                 }}
                              >
                                 <Stack gap="md">
                                    <ThemeIcon size={56} radius="md" style={{ background: card.color }}>
                                       <IconComponent size={28} color="white" />
                                    </ThemeIcon>
                                    <div>
                                       <Title order={4} fw={600} mb="xs">
                                          {card.title}
                                       </Title>
                                       <Text size="sm" c="dimmed" lineClamp={2}>
                                          {card.description}
                                       </Text>
                                    </div>
                                    <Button
                                       variant="light"
                                       rightSection={<IconArrowRight size={16} />}
                                       style={{ color: card.color }}
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(card.path);
                                       }}
                                    >
                                       View Report
                                    </Button>
                                 </Stack>
                              </Paper>
                           </motion.div>
                        </Grid.Col>
                     );
                  })}
               </Grid>
            </motion.div>
         </Stack>
      </Container>
   );
}
